import { api } from '@packages/backend/convex/_generated/api';
import { type DiscountCodeCvx, getUserErrorMessage } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionDropdown } from '@/components/ActionDropdown';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ActionButton } from '@/components/ui+/ActionButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { DiscountCodeForm } from './components/DiscountCodeForm';

export const Route = createFileRoute('/r/$restaurantId/s/discount-codes')({
  component: Component,
});

function Component() {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();
  const { data: discountCodes, isPending } = useQuery(api.discountCodes.listByRestaurantId, {
    restaurantId,
  });
  const [formState, setFormState] = useState<DiscountCodeCvx | 'new' | 'closed'>('closed');

  const handleDelete = async (discountCodeId: string) => {
    try {
      await convex.mutation(api.discountCodes.deleteCode, { discountCodeId, restaurantId });
      toast.success('Deleted successfully');
    } catch (error) {
      const description = getUserErrorMessage(error);
      toast.error('Unable to delete!', { description });
    }
  };

  if (isPending) return <LoadingIcon />;

  return (
    <>
      {formState !== 'closed' && (
        <DiscountCodeForm
          editDiscountCode={formState === 'new' ? undefined : formState}
          onClose={() => setFormState('closed')}
          restaurantId={restaurantId}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Discount codes</CardTitle>
          <CardDescription>
            Create promo codes that customers can apply at checkout for a percentage discount.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {discountCodes && discountCodes.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Usage Limit</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {discountCodes.map((dc) => (
                  <TableRow key={dc._id}>
                    <TableCell className="py-0 font-mono font-medium">{dc.code}</TableCell>
                    <TableCell className="py-0">{dc.discountPercent}%</TableCell>
                    <TableCell className="py-0">
                      <Badge variant={dc.isActive ? 'default' : 'secondary'}>
                        {dc.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-0">
                      {dc.usageLimitPerCustomer
                        ? `${dc.usageLimitPerCustomer} per customer`
                        : 'Unlimited'}
                    </TableCell>
                    <TableCell className="py-0">
                      <ActionDropdown
                        onDelete={() => handleDelete(dc._id)}
                        onEdit={() => setFormState(dc)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <ActionButton className="mt-4" onClick={() => setFormState('new')} variant="outline">
            Add discount code
          </ActionButton>
        </CardContent>
      </Card>
    </>
  );
}
